import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { QueryState } from '../../components/QueryState.js';

const TYPE_LABEL: Record<string, string> = {
  FIND_LOCATION: 'Find location',
  AVAILABILITY_CHECK: 'Availability check',
  MORE_PHOTOS: 'More photos',
  INFORMATION: 'Information',
  TECHNICAL_FEASIBILITY: 'Technical feasibility',
  RECCE: 'Recce',
  // Named for what it IS — no canonical hold exists, and calling it "Hold"
  // would promise a booking action nothing performs.
  HOLD_COORDINATION: 'Hold coordination',
  PERMIT_QUESTION: 'Permit question',
  REPLACEMENT: 'Replacement',
};

const STATUS_STYLE: Record<string, [string, string]> = {
  DRAFT: ['#F2F4F7', '#667085'],
  SENT: ['#EAECF0', '#344054'],
  IN_PROGRESS: ['#FDF2CC', '#B54708'],
  SCHEDULED: ['#F2F4F7', '#475467'],
  ANSWERED: ['#D1FADF', '#067647'],
  CANCELLED: ['#F2F4F7', '#98A2B3'],
};

export function RequestsTab({ projectId }: { projectId: string }) {
  const requests = useQuery({
    queryKey: ['requests', projectId],
    queryFn: () => api.listRequests(projectId),
  });

  return (
    <div data-testid="requests-tab">
      <p className="dh" style={{ fontSize: 22 }}>
        Requests
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6, maxWidth: '68ch' }}>
        Finding locations, availability, recces and permit questions. Every request carries what it points
        at, who owns it and when it is due.
      </p>

      <QueryState query={requests} subject="requests" testId="requests" />

      {requests.data && requests.data.length === 0 ? (
        <p className="mm" style={{ fontSize: 13, marginTop: 18 }} data-testid="requests-empty">
          Nothing raised yet.
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
        {requests.data?.map((r) => {
          const [bg, fg] = STATUS_STYLE[r.status] ?? STATUS_STYLE.DRAFT!;
          return (
            <div key={r.id} className="card1" style={{ padding: '14px 16px' }} data-testid={`request-${r.id}`}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className="chip" data-testid={`reference-${r.id}`}>
                  {r.reference}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700 }}>{r.title}</span>
                <span className="stag" style={{ background: bg, color: fg }}>
                  {r.status.toLowerCase().replace('_', ' ')}
                </span>
                <span className="mm" style={{ fontSize: 11.5, marginLeft: 'auto' }}>
                  {TYPE_LABEL[r.type] ?? r.type}
                </span>
              </div>

              {r.targets.length > 0 ? (
                <p className="mm" style={{ margin: '8px 0 0', fontSize: 11.5 }}>
                  {/* The role matters: a SUBJECT location and an ALTERNATIVE are
                      the same type playing different parts. */}
                  About: {r.targets.map((t) => `${t.targetType.toLowerCase()} (${t.targetRole.toLowerCase()})`).join(' · ')}
                </p>
              ) : null}

              {r.result ? (
                <p style={{ margin: '8px 0 0', fontSize: 12.5, fontWeight: 600, color: 'var(--ok-dark)' }}>
                  Result: {r.result}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
